import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Send, Sparkles, X } from 'lucide-react';
import { SITE } from '@/config/site';

const API = process.env.REACT_APP_BACKEND_URL || '';

const GREETING =
  'Bienvenido. Soy el concierge de Cristian Cruz: te ayudo a encontrar tu próxima fragancia o a dar el primer paso en el negocio de la reventa. ¿En qué te sirvo?';

const CHIPS = ['Recomiéndame un perfume', '¿Cómo empiezo en la reventa?', 'Quiero unirme a la comunidad'];

export default function Concierge() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([{ role: 'assistant', content: GREETING }]);
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (text) => {
    const content = (text || input).trim();
    if (!content || busy) return;
    setInput('');
    const history = [...messages, { role: 'user', content }];
    setMessages([...history, { role: 'assistant', content: '' }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId || undefined, messages: history.map((m) => ({ role: m.role, content: m.content })) }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      let acc = '';
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const blocks = buf.split('\n\n');
        buf = blocks.pop();
        for (const block of blocks) {
          const line = block.trim();
          if (!line.startsWith('data:')) continue;
          const evt = JSON.parse(line.slice(5));
          if (evt.type === 'session') setSessionId(evt.session_id);
          if (evt.type === 'delta') {
            acc += evt.content;
            setMessages((prev) => {
              const next = [...prev];
              next[next.length - 1] = { role: 'assistant', content: acc };
              return next;
            });
          }
        }
      }
    } catch (e) {
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = { role: 'assistant', content: 'Lo siento, hubo un problema de conexión. Inténtalo de nuevo en un momento.' };
        return next;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <motion.button
        data-testid="concierge-open"
        type="button"
        aria-label={open ? 'Cerrar concierge' : 'Abrir concierge'}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.6, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-5 right-5 z-[80] flex h-14 w-14 items-center justify-center rounded-full border border-gold/40 bg-obsidian/90 text-gold shadow-[0_0_30px_rgba(212,175,55,0.18)] backdrop-blur-md transition-colors duration-300 hover:bg-gold hover:text-noir"
      >
        {open ? <X size={20} /> : <Sparkles size={20} />}
      </motion.button>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="concierge-panel"
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 left-4 right-4 z-[80] flex max-h-[68vh] flex-col overflow-hidden rounded-3xl border border-white/10 bg-obsidian/[0.97] backdrop-blur-xl sm:left-auto sm:w-[380px]"
          >
            <header className="flex items-center justify-between border-b border-white/5 px-5 py-4">
              <div>
                <p className="text-[10px] uppercase tracking-[0.35em] text-cream/90">Concierge</p>
                <p className="mt-1 text-[9px] uppercase tracking-[0.25em] text-gold">Cristian Cruz · En línea</p>
              </div>
              <a
                data-testid="concierge-whatsapp"
                href={SITE.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-full border border-gold/40 px-3 py-1.5 text-[9px] uppercase tracking-[0.2em] text-gold transition-colors duration-300 hover:bg-gold hover:text-noir"
              >
                Quiero empezar
              </a>
            </header>
            <div ref={listRef} className="flex-1 space-y-3 overflow-y-auto px-5 py-4" data-testid="concierge-messages">
              {messages.map((m, i) => (
                <div key={i} data-testid={`concierge-message-${m.role}`} className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
                  <p
                    className={`max-w-[85%] rounded-2xl px-4 py-3 text-[13px] leading-relaxed ${
                      m.role === 'user' ? 'rounded-br-sm bg-cream text-noir' : 'rounded-bl-sm border border-white/10 bg-charcoal text-cream/90'
                    }`}
                  >
                    {m.content || '· · ·'}
                  </p>
                </div>
              ))}
            </div>
            {messages.length <= 2 && (
              <div className="flex gap-2 overflow-x-auto px-5 pb-3" data-testid="concierge-chips">
                {CHIPS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    data-testid="concierge-chip"
                    onClick={() => send(c)}
                    className="whitespace-nowrap rounded-full border border-white/15 px-3.5 py-2 text-[11px] text-cream/80 transition-colors duration-300 hover:border-gold hover:text-gold"
                  >
                    {c}
                  </button>
                ))}
              </div>
            )}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="flex items-center gap-3 border-t border-white/5 px-5 py-4"
            >
              <input
                data-testid="concierge-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Escribe tu mensaje..."
                className="h-11 flex-1 bg-transparent text-sm text-cream placeholder:text-cream/30 focus:outline-none"
              />
              <button
                type="submit"
                data-testid="concierge-send"
                disabled={busy || !input.trim()}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cream text-noir transition-colors duration-300 hover:bg-gold disabled:opacity-30"
              >
                <Send size={15} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
